import type { ReactNode } from "react"
import { FUNDO, type Tom } from "./tons"

// As peças de que as seções da Visão são feitas. Ficam fora da página porque a seção da margem é
// um componente próprio — tem busca própria, para uma falha do townhall não derrubar a página — e
// precisa desenhar igual ao resto.

export const Kpi = ({
    rotulo,
    valor,
    nota,
    tom,
    destaque,
}: {
    rotulo: string
    valor: string
    nota?: string
    // O tom pinta só a nota de rodapé. O número grande fica sempre em tinta cheia: uma contagem
    // não é boa nem ruim por si, quem qualifica é a frase embaixo dela.
    tom?: Tom
    destaque?: boolean
}) => (
    <div
        className={
            destaque ? "rounded-lg bg-brand p-4" : "rounded-lg border border-line bg-card p-4"
        }
    >
        <p
            className={
                destaque
                    ? "text-xs font-semibold text-brand-ink/80"
                    : "text-xs font-semibold text-soft"
            }
        >
            {rotulo}
        </p>
        <p
            className={
                destaque
                    ? "mt-1 font-num text-2xl font-bold tabular-nums text-brand-ink"
                    : "mt-1 font-num text-2xl font-bold tabular-nums text-ink"
            }
        >
            {valor}
        </p>
        {nota && (
            <p
                className={
                    destaque
                        ? "mt-1 flex items-center gap-1.5 text-xs text-brand-ink/80"
                        : "mt-1 flex items-center gap-1.5 text-xs text-faint"
                }
            >
                {tom && !destaque && (
                    <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${FUNDO[tom]}`} />
                )}
                {nota}
            </p>
        )}
    </div>
)

export const Barras = ({
    linhas,
    rotulosLongos,
}: {
    linhas: { rotulo: string; valor: number; nota?: string; tom?: Tom }[]
    // Motivos são frases, e não faixas: cortados em "Tempo de casa insu…" deixam de dizer o motivo.
    // Aqui o rótulo ganha coluna larga e quebra linha em vez de truncar.
    rotulosLongos?: boolean
}) => {
    const maior = Math.max(...linhas.map((linha) => linha.valor), 1)
    return (
        <ul className="space-y-2">
            {linhas.map((linha) => (
                <li
                    key={linha.rotulo}
                    className={`grid items-center gap-3 text-sm ${
                        rotulosLongos
                            ? "grid-cols-[minmax(0,16rem)_1fr_auto]"
                            : "grid-cols-[minmax(0,9rem)_1fr_auto]"
                    }`}
                >
                    <span className={rotulosLongos ? "text-soft" : "truncate text-soft"}>
                        {linha.rotulo}
                    </span>
                    <span className="h-2.5 overflow-hidden rounded-full bg-sunk">
                        <span
                            className={`block h-full rounded-full ${FUNDO[linha.tom ?? "dado"]}`}
                            style={{
                                width: `${linha.valor ? Math.max(3, (linha.valor / maior) * 100) : 0}%`,
                            }}
                        />
                    </span>
                    <span className="tabular-nums font-semibold text-ink">
                        {linha.valor}
                        {linha.nota && (
                            <span className="ml-1 font-normal text-faint">{linha.nota}</span>
                        )}
                    </span>
                </li>
            ))}
        </ul>
    )
}

export const Secao = ({
    titulo,
    descricao,
    larga,
    children,
}: {
    titulo: string
    descricao?: string
    larga?: boolean
    children: ReactNode
}) => (
    <section
        className={`rounded-lg border border-line bg-card p-5 ${larga ? "lg:col-span-2" : ""}`}
    >
        <h2 className="font-display font-semibold text-ink">{titulo}</h2>
        {descricao && <p className="mt-0.5 text-sm text-faint">{descricao}</p>}
        <div className="mt-4">{children}</div>
    </section>
)
